import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Drawer } from "antd";
import Button from "@dtci/spellbook/Button";
import { useWindowsActions } from "tap/store/windows/useWindows.js";
import { tapApi } from "tap/api.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { WindowSequenceForm } from "../window-sequence-form";
import {
  formatFormDataForSave,
  validateForm,
  transformWindowSequenceToFormData,
} from "../utils/formUtils";

export const EditWindowSequenceDrawer = () => {
  const { isEditDrawerOpen, selectedWindowSequence, windowSequenceForm } =
    useSelector((state) => state.windows);
  const { updateWindows, clearWindowForm } = useWindowsActions();
  const { showSuccessToast, showErrorToast } = useToasts();
  const { setLoadingTrue, setLoadingFalse } = useLoading();

  const [validationErrors, setValidationErrors] = useState({});
  const [saveDisabled, setSaveDisabled] = useState(false);

  useEffect(() => {
    if (isEditDrawerOpen && selectedWindowSequence?.id) {
      setValidationErrors({});
      updateWindows({
        windowSequenceForm: transformWindowSequenceToFormData(
          selectedWindowSequence,
        ),
      });
    }
  }, [isEditDrawerOpen, selectedWindowSequence]);

  const handleClose = () => {
    setValidationErrors({});
    updateWindows({
      isEditDrawerOpen: false,
    });
  };

  // Form reset deferred to afterOpenChange to allow close animation to complete
  const handleAfterOpenChange = (open) => {
    if (!open) {
      clearWindowForm();
    }
  };

  const handleSave = async () => {
    const formData = windowSequenceForm;

    const errors = validateForm(formData, "edit");
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showErrorToast("Please fix validation errors before saving");
      return;
    }

    setLoadingTrue();

    try {
      const payload = formatFormDataForSave(
        formData,
        selectedWindowSequence,
        false,
      );
      const result = await tapApi.updateWindowSequence(
        selectedWindowSequence.id,
        payload,
      );

      const hasApiFieldErrors =
        result?.errors?.windowSequenceName || result?.errors?.windowsError;
      if (result?.error || hasApiFieldErrors) {
        // Map API field errors to form validation errors
        const newErrors = {};
        if (result?.errors?.windowSequenceName) {
          newErrors.windowSequenceName = result.errors.windowSequenceName;
        }
        if (result?.errors?.windowsError) {
          newErrors.windows = result.errors.windowsError;
        }
        if (Object.keys(newErrors).length > 0) {
          setValidationErrors(newErrors);
        }
        const errorMsg =
          result?.errors?.windowSequenceName ||
          result?.errors?.windowsError ||
          result?.error?.message ||
          "Failed to update window sequence";
        showErrorToast(errorMsg);
      } else {
        showSuccessToast("Window sequence updated successfully");
        handleClose();
        updateWindows({ refreshWindows: true });
      }
    } catch (error) {
      console.error("Error updating window sequence:", error);
      showErrorToast("An error occurred while updating the window sequence");
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <Drawer
      className="create-drawer windows-edit-drawer"
      title={`Edit Window Sequence - ${selectedWindowSequence?.id || ""}`}
      width={900}
      placement="right"
      open={isEditDrawerOpen}
      onClose={handleClose}
      afterOpenChange={handleAfterOpenChange}
      destroyOnClose
      bodyStyle={{ padding: 0, backgroundColor: "#f5f5f5" }}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            iconType="fa-solid fa-xmark"
            styleType="link"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            iconType="fa-solid fa-save"
            styleType="link"
            onClick={handleSave}
            disabled={saveDisabled}
          >
            Save
          </Button>
        </div>
      }
    >
      <WindowSequenceForm
        mode="edit"
        validationErrors={validationErrors}
        onNextWindowIssueChange={setSaveDisabled}
      />
    </Drawer>
  );
};
